import {toast} from '@wandb/weave/common/components/elements/Toast';
import * as Panel2 from '@wandb/weave/components/Panel2/panel';
import {
  connectionToNodes,
  opArtifactMembershipArtifactVersion,
  opArtifactMembershipCollection,
  opArtifactProject,
  opArtifactVersionId,
  opArtifactVersionRawTags,
  opDict,
  opEntityName,
  opProjectEntity,
  opProjectName,
} from '@wandb/weave/core';
import {useValue} from '@wandb/weave/react';
import * as React from 'react';

import {
  useCreateArtifactTagAssignmentsMutation,
  useDeleteArtifactTagAssignmentsMutation,
  useEntityTagsQuery,
} from '../../../generated/graphql';
import {ENTITY_TAGS_QUERY} from '../../../state/graphql/entityTagsQuery';
// eslint-disable-next-line wandb/no-deprecated-imports
import * as GQLTypes from '../../../types/graphql';
import {getErrMsgFromError, propagateErrorsContext} from '../../../util/errors';
import {useIsProjectReadOnly} from '../../Registries/common/hooks';
import {
  AddTagAssignmentButton,
  createAttributesString,
  mapGQLTags,
  matchTag,
  ModifiedTag,
  parseTag,
  TagAssignment,
  TagAttributes,
} from '../../TagAssignment';
import {ArtifactTagList, TagListProps} from '../artifact/PanelArtifactTagList';

const inputType = 'artifactMembership' as const;
type ConfigType = {};

export const PanelArtifactMembershipOverviewTagList: React.FC<
  Panel2.PanelProps<typeof inputType, ConfigType> & TagListProps
> = props => {
  const artifactMembershipNode = props.input;

  const overviewValue = useValue(
    React.useMemo(() => {
      const artifactVersionNode = opArtifactMembershipArtifactVersion({
        artifactMembership: artifactMembershipNode,
      });
      const projectNode = opArtifactProject({
        artifact: opArtifactMembershipCollection({
          artifactMembership: artifactMembershipNode,
        }),
      });
      return opDict({
        projectName: opProjectName({
          project: projectNode,
        }),
        entityName: opEntityName({
          entity: opProjectEntity({project: projectNode}),
        }),
        artifactVersionId: opArtifactVersionId({
          artifactVersion: artifactVersionNode,
        }),
        rawTags: opArtifactVersionRawTags({
          artifactVersion: artifactVersionNode,
        }),
      } as any);
    }, [artifactMembershipNode])
  );

  const currentTagsRaw: Array<{
    id: string;
    name: string;
    tagCategoryName: string;
    attributes: string;
  }> = React.useMemo(
    () => overviewValue.result?.rawTags ?? [],
    [overviewValue.result?.rawTags]
  );

  const currentTags = mapGQLTags(currentTagsRaw);

  const entityTagsQuery = useEntityTagsQuery({
    // entityTags are only needed while rendering the list of tags
    // when a user tries to add a new tag. So the query result is not
    // needed if we're rendering the list without the 'add' button
    skip: !overviewValue.result?.entityName || !props.showButton,
    variables: {
      entityName: overviewValue.result?.entityName,
    },
  });
  const {isProjectReadOnly: readOnly, loading: isProjectReadOnlyLoading} =
    useIsProjectReadOnly(
      overviewValue.result?.entityName ?? '',
      overviewValue.result?.projectName ?? ''
    );

  const entity = entityTagsQuery.data?.entity;
  const allTags = React.useMemo(() => {
    const rawTags =
      entity?.tagCategories?.flatMap(tc => connectionToNodes(tc.tags)) ?? [];
    return mapGQLTags(rawTags);
  }, [entity]);

  if (
    overviewValue.loading ||
    overviewValue.result == null ||
    isProjectReadOnlyLoading
  ) {
    return <></>;
  }

  // We don't want to display the error message in case of the
  // entity query failing as they only list out existing tags on the collection
  if (entityTagsQuery.error) {
    const errMsg = getErrMsgFromError(entityTagsQuery.error);
    console.error(`Error retrieving entity tags; Msg: ${errMsg}`);
  }

  return (
    <PanelArtifactVersionTagListInner
      {...props}
      refresh={overviewValue.refresh}
      entityName={overviewValue.result.entityName}
      artifactVersionId={overviewValue.result.artifactVersionId}
      currentTags={currentTags}
      allTags={allTags}
      showButton={props.showButton && !readOnly}
    />
  );
};

type ArtifactVersionTagListInnerProps = {
  refresh: () => void;
  entityName: string;
  artifactVersionId: string;
  currentTags: ModifiedTag[];
  allTags: ModifiedTag[];
};

export const PanelArtifactVersionTagListInner: React.FC<
  ArtifactVersionTagListInnerProps & TagListProps
> = props => {
  const [addTagAssignment] = useCreateArtifactTagAssignmentsMutation({
    context: propagateErrorsContext(),
    refetchQueries: [
      {
        query: ENTITY_TAGS_QUERY,
        variables: {entityName: props.entityName},
      },
    ],
  });

  const createTagAssignment = React.useCallback(
    async (tagInput: GQLTypes.Tag) => {
      try {
        const tagStr = tagInput.name;
        if (props.currentTags.some((t: ModifiedTag) => matchTag(t, tagStr))) {
          return Promise.resolve();
        }

        // Parse input string into tag and category
        const [tagCategoryName, tagName] = parseTag(tagStr);

        // Stringify Attributes
        const tagAttributes: TagAttributes = {
          color: tagInput.colorIndex,
          icon: tagInput.icon,
        };
        const attributesStr = createAttributesString(tagAttributes);
        await addTagAssignment({
          variables: {
            artifactID: props.artifactVersionId,
            tags: [
              {
                tagCategoryName,
                tagName,
                attributes: attributesStr,
              },
            ],
          },
        });
        props.refresh();
      } catch (error) {
        console.error(error);
        toast(
          'Something went wrong when trying to create this tag. Please check for special characters.'
        );
      }
    },
    [addTagAssignment, props]
  );

  const [removeTagAssignment] = useDeleteArtifactTagAssignmentsMutation({
    context: propagateErrorsContext(),
    refetchQueries: [
      {
        query: ENTITY_TAGS_QUERY,
        variables: {entityName: props.entityName},
      },
    ],
  });

  const deleteTagAssignment = React.useCallback(
    async (tag: ModifiedTag) => {
      try {
        await removeTagAssignment({
          variables: {
            artifactID: props.artifactVersionId,
            tags: [
              {
                tagName: tag.name,
                tagCategoryName: tag.tagCategoryName,
              },
            ],
          },
        });
        props.refresh();
      } catch (error) {
        console.error(error);
        toast('Something went wrong when trying to delete this tag.');
      }
      return;
    },
    [removeTagAssignment, props]
  );

  return (
    <ArtifactTagList wrapList={props.wrap}>
      {props.currentTags.map(tag => {
        return (
          <TagAssignment
            key={tag.tagCategoryName + tag.name}
            tag={tag}
            showColor={props.showColor}
            size={props.tagSize}
            canDelete={props.showButton}
            onDelete={() => deleteTagAssignment(tag)}
          />
        );
      })}
      {props.showButton && (
        // Workaround: Prevent clipping of left edge of AddTagAssignmentButton if there are no assigned tags.
        //  See `marginLeft` value here: https://github.com/wandb/weave/blob/52ca63da5cc4c07ab4a5ca8e41203b10a5e061cf/weave-js/src/common/components/Tags.tsx#L70
        <div style={{marginLeft: props.currentTags ? '2px' : undefined}}>
          <AddTagAssignmentButton
            assignedTags={props.currentTags}
            availableTags={props.allTags}
            onCreate={createTagAssignment}
          />
        </div>
      )}
    </ArtifactTagList>
  );
};
